import { useCallback, useEffect, useState } from 'react'
import { ChevronDown, ChevronRight, Pencil, Pin, PinOff, Trash2 } from 'lucide-react'
import { PROJECT_NOTE_COLORS, erfuelltRecht } from '@symphenia/shared-types'
import { apiErrorText } from '@/lib/api-error'
import { formCard, inlineInput, textareaBase } from '@/lib/form-styles'
import { useAuthStore } from '@/lib/auth.store'
import { api } from '@/lib/axios'
import { fetchUsers } from '../api/projects-head.api'
import {
  completeTask,
  createJournalEntry,
  createTask,
  deleteJournalEntry,
  deleteTask,
  fetchJournal,
  fetchProjectTasks,
  journalEntryToTask,
  pinJournalEntry,
  snoozeTask,
  unpinJournalEntry,
  updateJournalEntry,
  updateTask,
} from '../api/projects-phase-d.api'
import { NOTE_COLOR_CLASSES, NOTE_COLOR_LABELS, alsTag, alsZeitpunkt } from '../project-labels'
import type { JournalEntry, ProjectTask } from '../types/phase-d.types'
import type { LookupUser, ProjectDetail } from '../types/project.types'
import { bestaetige } from '@/lib/dialoge'

interface JournalTyp {
  id: string
  key: string
  name: string
  isSystem: boolean
  isActive: boolean
}

const feld = inlineInput
/** `datetime-local` braucht die lokale Wandzeit ohne Zone. */
const lokal = (iso?: string | null) => {
  if (!iso) return ''
  const d = new Date(iso)
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
}
const tagIn = (tage: number) => {
  const d = new Date()
  d.setDate(d.getDate() + tage)
  return d.toISOString().slice(0, 10)
}

/**
 * AP-35 Detailblatt 9 6.1 — **Der Reiter Journal & Aufgaben.**
 *
 * Links die Zeitleiste (Hand und System in **einer** Reihenfolge), rechts oben
 * die offenen Aufgaben, rechts unten die angepinnten Notizen. Das ist kein
 * Layout-Geschmack, sondern die Aussage des Modells: Notiz, Post-it und Aufgabe
 * sind **Sichten auf dasselbe** (Entscheidung 20), nicht drei Listen.
 *
 * **Systemzeilen sind gedämpft und unveränderlich.** Sie sind die Geschichte des
 * Projekts, nicht Kommentar — wer sie ändern könnte, könnte das Protokoll
 * umschreiben. Einträge von Hand haben einen Stift (Auftraggeber 18.9.2026),
 * Aufgaben tragen Fälligkeit, Zuständigen und Erinnerung — vorher entstanden
 * sie nur mit einem Titel, und „Meine Aufgaben" blieb leer.
 */
export function ProjectJournalTab({ projekt }: { projekt: ProjectDetail }) {
  const { user } = useAuthStore()
  const darfSchreiben = erfuelltRecht('projects.journal.manage', user?.permissions)

  const [typen, setTypen] = useState<JournalTyp[]>([])
  const [benutzer, setBenutzer] = useState<LookupUser[]>([])
  const [eintraege, setEintraege] = useState<JournalEntry[]>([])
  const [aufgaben, setAufgaben] = useState<ProjectTask[]>([])
  const [art, setArt] = useState<'all' | 'hand' | 'system'>('all')
  const [typId, setTypId] = useState('')
  const [text, setText] = useState('')
  const [wann, setWann] = useState('')
  const [anpinnen, setAnpinnen] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  const laden = useCallback(async () => {
    try {
      const [journal, liste] = await Promise.all([fetchJournal(projekt.id, { kind: art, limit: 100 }), fetchProjectTasks(projekt.id)])
      setEintraege(journal.data)
      setAufgaben(liste)
      setFehler(null)
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    }
  }, [projekt.id, art])

  useEffect(() => {
    void laden()
  }, [laden])

  useEffect(() => {
    void api
      .get<{ data: JournalTyp[] }>('/projects/settings/journal-types')
      .then((res) => {
        const wahl = (res.data.data ?? []).filter((t) => t.isActive && !t.isSystem)
        setTypen(wahl)
        setTypId((alt) => alt || wahl[0]?.id || '')
      })
      .catch(() => setTypen([]))
    void fetchUsers().then(setBenutzer).catch(() => undefined)
  }, [])

  async function eintragen() {
    if (!text.trim() || !typId) return
    try {
      await createJournalEntry(projekt.id, {
        typeId: typId,
        text: text.trim(),
        isPinned: anpinnen,
        // Rückdatierbar, nie in der Zukunft (Blatt 9 2.2) — der Server prüft es.
        ...(wann ? { occurredAt: new Date(wann).toISOString() } : {}),
      })
      setText('')
      setWann('')
      setAnpinnen(false)
      await laden()
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    }
  }

  async function fuehreAus(aktion: () => Promise<unknown>) {
    setFehler(null)
    try {
      await aktion()
      await laden()
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    }
  }

  const angepinnt = eintraege.filter((e) => e.isPinned)

  return (
    <div className="space-y-3">
      {fehler && <div className="text-sm text-destructive">{fehler}</div>}

      <div className="flex flex-col gap-3 lg:flex-row">
        {/* ── Zeitleiste ────────────────────────────────────────────────── */}
        <div className={`${formCard} min-w-0 flex-1`}>
          {darfSchreiben && (
            <div className="space-y-2 border-b border-border pb-3">
              <div className="flex flex-wrap gap-1">
                {typen.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setTypId(t.id)}
                    className={`rounded-full px-2.5 py-1 text-xs ${typId === t.id ? 'bg-primary text-primary-foreground' : 'border border-border'}`}
                  >
                    {t.name}
                  </button>
                ))}
              </div>
              <textarea className={textareaBase} rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Was ist passiert?" />
              <div className="flex flex-wrap items-center gap-2">
                <label className="flex items-center gap-1 text-xs text-muted-foreground">
                  wann
                  <input type="datetime-local" className={`${feld} w-auto`} value={wann} max={lokal(new Date().toISOString())} onChange={(e) => setWann(e.target.value)} title="leer = jetzt" />
                </label>
                <label className="flex items-center gap-1 text-xs">
                  <input type="checkbox" checked={anpinnen} onChange={(e) => setAnpinnen(e.target.checked)} />
                  anpinnen
                </label>
                <button type="button" disabled={!text.trim() || !typId} className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground disabled:opacity-50" onClick={eintragen}>
                  Eintragen
                </button>
                <button
                  type="button"
                  disabled={!text.trim()}
                  className="rounded-md border border-border px-3 py-1.5 text-sm disabled:opacity-50"
                  onClick={async () => {
                    await fuehreAus(() => createTask(projekt.id, { title: text.trim().slice(0, 200) }))
                    setText('')
                  }}
                >
                  → Aufgabe
                </button>
              </div>
            </div>
          )}

          <div className="mt-3 flex items-center gap-2 text-xs">
            {(['all', 'hand', 'system'] as const).map((wert) => (
              <button key={wert} type="button" onClick={() => setArt(wert)} className={`rounded-full px-2 py-0.5 ${art === wert ? 'bg-accent font-medium' : 'text-muted-foreground'}`}>
                {wert === 'all' ? 'alle' : wert === 'hand' ? 'von Hand' : 'System'}
              </button>
            ))}
          </div>

          <ul className="mt-3 max-h-[60vh] space-y-2 overflow-y-auto">
            {eintraege.length === 0 && <li className="text-sm text-muted-foreground">Noch nichts eingetragen.</li>}
            {eintraege.map((e) => (
              <Eintrag key={e.id} e={e} projectId={projekt.id} typen={typen} darfSchreiben={darfSchreiben} fuehreAus={fuehreAus} />
            ))}
          </ul>
        </div>

        {/* ── Aufgaben und Angepinntes ──────────────────────────────────── */}
        <div className="space-y-3 lg:w-[360px] lg:shrink-0">
          <Aufgaben projectId={projekt.id} aufgaben={aufgaben} benutzer={benutzer} darfSchreiben={darfSchreiben} fuehreAus={fuehreAus} />

          <div className={formCard}>
            <h3 className="mb-2 text-sm font-medium">Angepinnt</h3>
            <ul className="space-y-2">
              {angepinnt.length === 0 && <li className="text-xs text-muted-foreground">Nichts angepinnt.</li>}
              {angepinnt.map((e) => (
                <li key={e.id} className={`rounded-md border p-2 text-sm ${NOTE_COLOR_CLASSES[e.color ?? 'YELLOW']}`}>
                  <p className="whitespace-pre-line">{e.text}</p>
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    {e.authorName ?? ''}
                    {e.pinnedUntil && ` · bis ${alsTag(e.pinnedUntil)}`}
                  </p>
                  {darfSchreiben && !e.isSystem && (
                    <div className="mt-1 flex flex-wrap items-center gap-1">
                      {PROJECT_NOTE_COLORS.map((c) => (
                        <button
                          key={c}
                          type="button"
                          title={NOTE_COLOR_LABELS[c] ?? c}
                          aria-label={`Farbe ${NOTE_COLOR_LABELS[c] ?? c}`}
                          className={`h-4 w-4 rounded-full border ${NOTE_COLOR_CLASSES[c]} ${e.color === c ? 'ring-2 ring-ring' : ''}`}
                          onClick={() => fuehreAus(() => updateJournalEntry(projekt.id, e.id, { color: c }))}
                        />
                      ))}
                      <label className="ml-auto flex items-center gap-1 text-[11px] text-muted-foreground">
                        bis
                        <input
                          type="date"
                          className="rounded border border-border bg-background px-1 text-[11px]"
                          value={e.pinnedUntil?.slice(0, 10) ?? ''}
                          onChange={(ev) =>
                            ev.target.value &&
                            fuehreAus(() => pinJournalEntry(projekt.id, e.id, { color: e.color ?? 'YELLOW', pinnedUntil: ev.target.value }))
                          }
                        />
                      </label>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Ein Journaleintrag ─────────────────────────────────────────────────────

function Eintrag({
  e,
  projectId,
  typen,
  darfSchreiben,
  fuehreAus,
}: {
  e: JournalEntry
  projectId: string
  typen: JournalTyp[]
  darfSchreiben: boolean
  fuehreAus: (a: () => Promise<unknown>) => Promise<void>
}) {
  const [bearbeiten, setBearbeiten] = useState(false)
  const [w, setW] = useState({ text: e.text, typeId: e.typeId, occurredAt: lokal(e.occurredAt) })

  async function speichern() {
    const d: Record<string, unknown> = {}
    if (w.text.trim() && w.text.trim() !== e.text) d.text = w.text.trim()
    if (w.typeId !== e.typeId) d.typeId = w.typeId
    if (w.occurredAt && w.occurredAt !== lokal(e.occurredAt)) d.occurredAt = new Date(w.occurredAt).toISOString()
    if (Object.keys(d).length) await fuehreAus(() => updateJournalEntry(projectId, e.id, d))
    setBearbeiten(false)
  }

  return (
    <li className="border-b border-border pb-2 last:border-0">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-xs text-muted-foreground">
            {alsZeitpunkt(e.occurredAt)} · {e.type?.name ?? ''}
            {e.authorName ? ` · ${e.authorName}` : e.isSystem ? ' · System' : ''}
            {e.editedAt && ' · bearbeitet'}
            {e.position && ` · Pos. ${e.position.number}`}
          </p>
          {bearbeiten ? (
            <div
              className="mt-1 space-y-1"
              onKeyDown={(ev) => {
                if (ev.key === 'Escape') setBearbeiten(false)
                if (ev.key === 'Enter' && (ev.ctrlKey || ev.metaKey)) void speichern()
              }}
            >
              <textarea className={textareaBase} rows={3} autoFocus value={w.text} onChange={(ev) => setW({ ...w, text: ev.target.value })} />
              <div className="flex flex-wrap items-center gap-2">
                <select className={`${feld} w-auto`} value={w.typeId} onChange={(ev) => setW({ ...w, typeId: ev.target.value })}>
                  {!typen.some((t) => t.id === w.typeId) && <option value={w.typeId}>{e.type?.name ?? 'Typ'}</option>}
                  {typen.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
                <input type="datetime-local" className={`${feld} w-auto`} value={w.occurredAt} max={lokal(new Date().toISOString())} onChange={(ev) => setW({ ...w, occurredAt: ev.target.value })} />
                <button type="button" className="rounded-md border border-border px-2 py-1 text-xs" onClick={() => setBearbeiten(false)}>
                  Abbrechen
                </button>
                <button type="button" className="rounded-md bg-primary px-2 py-1 text-xs text-primary-foreground" onClick={() => void speichern()}>
                  Speichern
                </button>
              </div>
            </div>
          ) : (
            <p className="whitespace-pre-line text-sm text-foreground">{e.text}</p>
          )}
        </div>
        {darfSchreiben && !e.isSystem && !bearbeiten && (
          <span className="flex shrink-0 items-center gap-1.5">
            <button
              type="button"
              title="Bearbeiten"
              aria-label="Eintrag bearbeiten"
              onClick={() => {
                setW({ text: e.text, typeId: e.typeId, occurredAt: lokal(e.occurredAt) })
                setBearbeiten(true)
              }}
            >
              <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
            <button
              type="button"
              title={e.isPinned ? 'Abpinnen' : 'Anpinnen'}
              onClick={() => fuehreAus(() => (e.isPinned ? unpinJournalEntry(projectId, e.id) : pinJournalEntry(projectId, e.id, { color: 'YELLOW' })))}
            >
              {e.isPinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5 text-muted-foreground" />}
            </button>
            <button type="button" title="→ Aufgabe" className="text-xs underline" onClick={() => fuehreAus(() => journalEntryToTask(projectId, e.id, { title: e.text.split('\n')[0].slice(0, 200) }))}>
              Aufgabe
            </button>
            <button
              type="button"
              title="Löschen"
              className="text-destructive"
              onClick={async () => {
                if (await bestaetige('Eintrag löschen?')) void fuehreAus(() => deleteJournalEntry(projectId, e.id))
              }}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </span>
        )}
      </div>
    </li>
  )
}

// ── Aufgaben ───────────────────────────────────────────────────────────────

function Aufgaben({
  projectId,
  aufgaben,
  benutzer,
  darfSchreiben,
  fuehreAus,
}: {
  projectId: string
  aufgaben: ProjectTask[]
  benutzer: LookupUser[]
  darfSchreiben: boolean
  fuehreAus: (a: () => Promise<unknown>) => Promise<void>
}) {
  const [neu, setNeu] = useState({ title: '', dueAt: '', assigneeId: '' })
  const [erledigteZeigen, setErledigteZeigen] = useState(false)
  const offene = aufgaben.filter((a) => !a.doneAt)
  const erledigte = aufgaben.filter((a) => a.doneAt)

  async function anlegen() {
    if (!neu.title.trim()) return
    await fuehreAus(() =>
      createTask(projectId, {
        title: neu.title.trim(),
        ...(neu.dueAt ? { dueAt: neu.dueAt } : {}),
        ...(neu.assigneeId ? { assigneeId: neu.assigneeId } : {}),
      }),
    )
    setNeu({ title: '', dueAt: '', assigneeId: '' })
  }

  return (
    <div className={formCard}>
      <h3 className="mb-2 text-sm font-medium">Aufgaben</h3>
      {darfSchreiben && (
        <div
          className="mb-3 space-y-1 rounded-md border border-dashed border-border p-2"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
              e.preventDefault()
              void anlegen()
            }
          }}
        >
          <input className={feld} placeholder="neue Aufgabe — Titel, Enter legt an" value={neu.title} onChange={(e) => setNeu({ ...neu, title: e.target.value })} />
          <div className="flex gap-1">
            <input type="date" className={feld} title="fällig am" value={neu.dueAt} onChange={(e) => setNeu({ ...neu, dueAt: e.target.value })} />
            <select className={feld} value={neu.assigneeId} onChange={(e) => setNeu({ ...neu, assigneeId: e.target.value })}>
              <option value="">— zuständig —</option>
              {benutzer.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
      <ul className="space-y-1 text-sm">
        {offene.length === 0 && <li className="text-xs text-muted-foreground">Nichts offen.</li>}
        {offene.map((a) => (
          <Aufgabe key={a.id} a={a} benutzer={benutzer} darfSchreiben={darfSchreiben} fuehreAus={fuehreAus} />
        ))}
      </ul>
      {erledigte.length > 0 && (
        <div className="mt-3 border-t border-border pt-2">
          <button type="button" className="flex items-center gap-1 text-xs text-muted-foreground" onClick={() => setErledigteZeigen((x) => !x)}>
            {erledigteZeigen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />} {erledigte.length} erledigt
          </button>
          {erledigteZeigen && (
            <ul className="mt-1 space-y-1 text-sm">
              {erledigte.map((a) => (
                <li key={a.id} className="flex items-start justify-between gap-2 text-muted-foreground">
                  <span className="line-through">{a.title}</span>
                  {darfSchreiben && (
                    <button type="button" className="shrink-0 text-xs underline" onClick={() => fuehreAus(() => updateTask(a.id, { reopen: true }))}>
                      wieder öffnen
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

function Aufgabe({
  a,
  benutzer,
  darfSchreiben,
  fuehreAus,
}: {
  a: ProjectTask
  benutzer: LookupUser[]
  darfSchreiben: boolean
  fuehreAus: (x: () => Promise<unknown>) => Promise<void>
}) {
  const [offen, setOffen] = useState(false)
  const neu = () => ({
    title: a.title,
    description: a.description ?? '',
    dueAt: a.dueAt?.slice(0, 10) ?? '',
    assigneeId: a.assigneeId ?? '',
    remindAt: lokal(a.remindAt),
  })
  const [w, setW] = useState(neu)

  async function speichern() {
    const alt = neu()
    const d: Record<string, unknown> = {}
    if (w.title.trim() && w.title.trim() !== alt.title) d.title = w.title.trim()
    if (w.description !== alt.description) d.description = w.description.trim() || null
    if (w.dueAt !== alt.dueAt) d.dueAt = w.dueAt || null
    if (w.assigneeId !== alt.assigneeId) d.assigneeId = w.assigneeId || null
    if (w.remindAt !== alt.remindAt) d.remindAt = w.remindAt ? new Date(w.remindAt).toISOString() : null
    if (Object.keys(d).length) await fuehreAus(() => updateTask(a.id, d))
    setOffen(false)
  }

  return (
    <li className="rounded-md">
      <div className="flex items-start gap-2">
        {darfSchreiben && <input type="checkbox" className="mt-1" onChange={() => fuehreAus(() => completeTask(a.id))} aria-label={`${a.title} erledigen`} />}
        <button
          type="button"
          className="min-w-0 flex-1 text-left"
          onClick={() => {
            if (!darfSchreiben) return
            setW(neu())
            setOffen((x) => !x)
          }}
        >
          <span className={a.state === 'OVERDUE' ? 'text-destructive' : ''}>{a.title}</span>
          {(a.dueAt || a.assigneeName) && (
            <span className="block text-xs text-muted-foreground">
              {a.dueAt ? `fällig ${alsTag(a.dueAt)}` : ''}
              {a.dueAt && a.assigneeName ? ' · ' : ''}
              {a.assigneeName ?? ''}
            </span>
          )}
        </button>
      </div>
      {offen && (
        <div
          className="mt-1 space-y-1 rounded-md border border-border bg-muted/30 p-2"
          onKeyDown={(e) => {
            if (e.key === 'Escape') setOffen(false)
            if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
              e.preventDefault()
              void speichern()
            }
          }}
        >
          <input className={feld} autoFocus value={w.title} onChange={(e) => setW({ ...w, title: e.target.value })} />
          <textarea className={feld} rows={2} placeholder="Beschreibung" value={w.description} onChange={(e) => setW({ ...w, description: e.target.value })} />
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="w-20 shrink-0">fällig</span>
            <input type="date" className={feld} value={w.dueAt} onChange={(e) => setW({ ...w, dueAt: e.target.value })} />
          </label>
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="w-20 shrink-0">zuständig</span>
            <select className={feld} value={w.assigneeId} onChange={(e) => setW({ ...w, assigneeId: e.target.value })}>
              <option value="">— niemand —</option>
              {benutzer.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="w-20 shrink-0">erinnern</span>
            <input type="datetime-local" className={feld} value={w.remindAt} onChange={(e) => setW({ ...w, remindAt: e.target.value })} />
          </label>
          <div className="flex flex-wrap items-center gap-2 pt-1 text-xs">
            <span className="text-muted-foreground">später:</span>
            <button type="button" className="underline" onClick={() => fuehreAus(() => snoozeTask(a.id, tagIn(1)))}>
              +1 Tag
            </button>
            <button type="button" className="underline" onClick={() => fuehreAus(() => snoozeTask(a.id, tagIn(7)))}>
              +1 Woche
            </button>
            <button
              type="button"
              className="ml-auto text-destructive underline"
              onClick={async () => {
                if (await bestaetige(`Aufgabe „${a.title}" löschen?`)) void fuehreAus(() => deleteTask(a.id))
              }}
            >
              löschen
            </button>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" className="rounded-md border border-border px-2 py-1 text-xs" onClick={() => setOffen(false)}>
              Abbrechen
            </button>
            <button type="button" className="rounded-md bg-primary px-2 py-1 text-xs text-primary-foreground" onClick={() => void speichern()}>
              Speichern
            </button>
          </div>
        </div>
      )}
    </li>
  )
}
